/**
 * PoC 프로그램 수행 지원서 — layoutConfig(flatten된 layout) + layoutRows 를
 * 입력 폼이 아닌 서식(문서) 테이블로 표시 (PocProgramSupportPreview 와 동일 계열 스타일)
 */
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isLongTextField, isDateField } from '../utils/form';
import { toComparableDataKey } from '../utils/formAdapter';

function cellStr(v) {
  if (v == null) return '';
  if (typeof v === 'object') return '';
  return String(v);
}

function imgSrc(raw) {
  const str = cellStr(raw).trim();
  if (!str) return '';
  if (str.startsWith('data:') || str.startsWith('http') || str.startsWith('/')) return str;
  if (isNaN(Number(str))) return `/api/glow/files/view?fileId=${str}`;
  return `/api/glow/files/viewByUid/${str}`;
}

function arrayColumnsOf(arrayField) {
  return arrayField?.fields || arrayField?.columns || [];
}

/** 예상금액 셀 문자열에서 숫자만 합산(콤마·공백 제거 후 parse) */
function parseAmountForSum(raw) {
  const s = cellStr(raw).trim();
  if (!s) return 0;
  const normalized = s.replace(/[,，]/g, '').replace(/\s/g, '');
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatAmountSum(total) {
  if (!Number.isFinite(total)) return '0';
  if (Math.abs(total - Math.round(total)) < 1e-9) {
    return new Intl.NumberFormat('ko-KR').format(Math.round(total));
  }
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 }).format(total);
}

/** SECTION title 과 필드 label 비교용(공백만 정규화) */
function normalizeHeading(s) {
  if (s == null) return '';
  return String(s).trim().replace(/\s+/g, ' ');
}

function headingsEqual(a, b) {
  return normalizeHeading(a) === normalizeHeading(b);
}

/** SECTION 안 항목이 1개일 때, 라벨 추출(ROW 한 칸·스칼라 필드) */
function soleFieldHeadingForDedupe(sectionChild) {
  if (!sectionChild) return '';
  if (sectionChild.type === 'ARRAY' || sectionChild.dataType === 'ARRAY') return '';
  if (sectionChild.key) return sectionChild.label || sectionChild.key;
  if (sectionChild.type === 'ROW' && Array.isArray(sectionChild.fields) && sectionChild.fields.length === 1) {
    const f = sectionChild.fields[0];
    return f ? f.label || f.key || '' : '';
  }
  return '';
}

/** flattenLayout 이 필드 key 를 camelCase 로 바꾸므로 snake·camel 모두 동일 기준으로 비교 */
const SUPPORT_FUND_USAGE_PLAN_KEY = toComparableDataKey('support_fund_usage_plan');
const ESTIMATED_AMOUNT_KEY = toComparableDataKey('estimated_amount');

/** 최상위 블록이 지원비 세부 사용계획 ARRAY 하나뿐이면 통합 테이블 밖에 렌더 */
function isSupportFundUsagePlanBlock(block) {
  const items = block?.type === 'DIVIDER' ? block.items || [] : block?.fields || [];
  if (items.length !== 1) return false;
  const sole = items[0];
  if (sole?.type !== 'ARRAY' && sole?.dataType !== 'ARRAY') return false;
  return toComparableDataKey(sole.key) === SUPPORT_FUND_USAGE_PLAN_KEY;
}

/** 통합 테이블 밖: sole ARRAY 분기와 동일 내용(제목·○ 라벨·ArrayRowsTable) */
function SupportFundUsagePlanOutsideTable({ block, blockIdx, layoutRows }) {
  const items = block?.fields || [];
  const sole = items[0];
  if (!sole) return null;
  const rows = Array.isArray(layoutRows?.[blockIdx]) ? layoutRows[blockIdx] : [{}];
  const hasTitle =
    block?.type !== 'BARE' &&
    block?.title != null &&
    String(block.title).trim() !== '';

  return (
    <div className="support-fund-usage-outside" style={{ marginTop: '0.75rem' }}>
      {hasTitle ? (
        <div className="support-section-title" style={{ marginBottom: '0.35rem' }}>
          {block.title}
        </div>
      ) : null}
      {sole.label ? (
        <div className="support-bullet-title mb-1">○ {sole.label}</div>
      ) : null}
      <ArrayRowsTable arrayField={sole} rows={rows} />
    </div>
  );
}

function ArrayRowsTable({ arrayField, rows }) {
  const cols = arrayColumnsOf(arrayField);
  if (!cols.length) return null;
  const safeRows = Array.isArray(rows) && rows.length ? rows : [{}];

  const amountCol = cols.find((c) => c?.key && toComparableDataKey(c.key) === ESTIMATED_AMOUNT_KEY);
  const amountColKey = amountCol?.key;
  const isSupportFundPlan =
    arrayField?.key && toComparableDataKey(arrayField.key) === SUPPORT_FUND_USAGE_PLAN_KEY;
  const showEstimatedTotal = Boolean(isSupportFundPlan && amountColKey);
  const estimatedTotal = showEstimatedTotal
    ? safeRows.reduce((acc, r) => acc + parseAmountForSum(r?.[amountColKey]), 0)
    : 0;

  return (
    <table className="poc-table support-budget-table" style={{ width: '100%', marginTop: '0.35rem' }}>
      <thead>
        <tr>
          <th className="poc-label" style={{ width: '3rem' }}>
            연번
          </th>
          {cols.map((c) => (
            <th key={c.key} className="poc-label">
              {c.label || c.key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {safeRows.map((r, i) => (
          <tr key={i}>
            <td className="poc-value" style={{ textAlign: 'center' }}>
              {i + 1}
            </td>
            {cols.map((c) => (
              <td key={c.key} className="poc-value support-prewrap">
                {cellStr(r?.[c.key])}
              </td>
            ))}
          </tr>
        ))}
        {showEstimatedTotal ? (
          <tr className="support-budget-total-row">
            <td className="poc-value support-total-label">합계</td>
            {cols.map((c) =>
              c.key === amountColKey ? (
                <td key={c.key} className="poc-value support-total-amount support-prewrap" style={{ textAlign: 'right' }}>
                  {formatAmountSum(estimatedTotal)}
                </td>
              ) : (
                <td key={c.key} className="poc-value support-total-label" />
              )
            )}
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

function renderFieldRows(field, row0, keyPrefix, opts = {}) {
  const { suppressDuplicateTitle = false } = opts;
  const k = field.key;
  const v = row0?.[k];
  const label = field.label || k;

  if (field.type === 'IMAGE' || field.dataType === 'IMAGE_TYPE') {
    const src = imgSrc(v);
    if (suppressDuplicateTitle) {
      return (
        <tr key={keyPrefix}>
          <td className="poc-value" colSpan={4}>
            {src ? (
              <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }} />
            ) : (
              <span className="text-muted">—</span>
            )}
          </td>
        </tr>
      );
    }
    return (
      <tr key={keyPrefix}>
        <td className="poc-label">{label}</td>
        <td className="poc-value" colSpan={3}>
          {src ? (
            <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }} />
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
      </tr>
    );
  }

  if (field.type === 'BOOLEAN' || field.dataType === 'BOOLEAN') {
    if (suppressDuplicateTitle) {
      return (
        <tr key={keyPrefix}>
          <td className="poc-value" colSpan={4}>
            {v === true || v === 'Y' || v === 'true' ? '예' : v === false || v === 'N' ? '아니오' : cellStr(v) || '—'}
          </td>
        </tr>
      );
    }
    return (
      <tr key={keyPrefix}>
        <td className="poc-label">{label}</td>
        <td className="poc-value" colSpan={3}>
          {v === true || v === 'Y' || v === 'true' ? '예' : v === false || v === 'N' ? '아니오' : cellStr(v) || '—'}
        </td>
      </tr>
    );
  }

  if (
    (field.type === 'TEXT' || !field.type || field.type === 'DATE_TYPE') &&
    isLongTextField(field.key, field.label) &&
    !isDateField(field.key, field.label)
  ) {
    if (suppressDuplicateTitle) {
      return (
        <Fragment key={keyPrefix}>
          <tr>
            <td className="support-section-body support-prewrap" colSpan={4}>
              {cellStr(v) || '—'}
            </td>
          </tr>
        </Fragment>
      );
    }
    return (
      <Fragment key={keyPrefix}>
        <tr>
          <td className="support-section-title" colSpan={4}>
            {label}
          </td>
        </tr>
        <tr>
          <td className="support-section-body support-prewrap" colSpan={4}>
            {cellStr(v) || '—'}
          </td>
        </tr>
      </Fragment>
    );
  }

  if (suppressDuplicateTitle) {
    return (
      <tr key={keyPrefix}>
        <td className="poc-value support-prewrap" colSpan={4}>
          {cellStr(v) || '—'}
        </td>
      </tr>
    );
  }

  return (
    <tr key={keyPrefix}>
      <td className="poc-label">{label}</td>
      <td className="poc-value support-prewrap" colSpan={3}>
        {cellStr(v) || '—'}
      </td>
    </tr>
  );
}

function renderItemRows(item, row0, keyPath) {
  if (!item) return null;

  if (item.type === 'ROW' && Array.isArray(item.fields)) {
    const fs = item.fields.filter((f) => f?.key);
    if (fs.length === 0) return null;
    const hasArrayField = fs.some((f) => f?.type === 'ARRAY' || f?.dataType === 'ARRAY');
    if (hasArrayField) {
      return (
        <Fragment key={keyPath}>
          {fs.map((f, i) =>
            f?.type === 'ARRAY' || f?.dataType === 'ARRAY'
              ? renderItemRows(f, row0, `${keyPath}-arr-${i}`)
              : renderFieldRows(f, row0, `${keyPath}-r-${i}`)
          )}
        </Fragment>
      );
    }
    if (fs.length === 1) return renderFieldRows(fs[0], row0, `${keyPath}-r0`);

    if (fs.length >= 2) {
      const pairs = [];
      for (let i = 0; i < fs.length; i += 2) {
        const a = fs[i];
        const b = fs[i + 1];
        if (b) {
          pairs.push(
            <tr key={`${keyPath}-p-${i}`}>
              <td className="poc-label">{a.label || a.key}</td>
              <td className="poc-value support-prewrap">{cellStr(row0?.[a.key]) || '—'}</td>
              <td className="poc-label">{b.label || b.key}</td>
              <td className="poc-value support-prewrap">{cellStr(row0?.[b.key]) || '—'}</td>
            </tr>
          );
        } else {
          pairs.push(renderFieldRows(a, row0, `${keyPath}-p-${i}`));
        }
      }
      return <Fragment key={keyPath}>{pairs}</Fragment>;
    }
  }

  if (item.type === 'SECTION' && Array.isArray(item.fields)) {
    const sole = item.fields.length === 1 ? item.fields[0] : null;
    const soleHeading = soleFieldHeadingForDedupe(sole);
    const hideChildLabel =
      sole &&
      item.title != null &&
      String(item.title).trim() !== '' &&
      soleHeading &&
      headingsEqual(item.title, soleHeading);

    const renderDedupedChild = (sub, i) => {
      if (!hideChildLabel || i !== 0) return renderItemRows(sub, row0, `${keyPath}-s${i}`);
      if (sub.key) return renderFieldRows(sub, row0, `${keyPath}-s${i}`, { suppressDuplicateTitle: true });
      if (sub.type === 'ROW' && Array.isArray(sub.fields) && sub.fields.length === 1 && sub.fields[0]?.key) {
        return renderFieldRows(sub.fields[0], row0, `${keyPath}-s${i}`, { suppressDuplicateTitle: true });
      }
      return renderItemRows(sub, row0, `${keyPath}-s${i}`);
    };

    return (
      <Fragment key={keyPath}>
        {item.title && String(item.title).trim() !== '' && (
          <tr>
            <td className="support-section-title" colSpan={4}>
              {item.title}
            </td>
          </tr>
        )}
        {item.fields.map((sub, i) => renderDedupedChild(sub, i))}
      </Fragment>
    );
  }

  if (item.type === 'GROUP' && Array.isArray(item.fields)) {
    const sep = item.separator ?? ' ~ ';
    const parts = item.fields
      .map((f) => {
        if (!f?.key) return '';
        const label = f.label ? `${f.label}: ` : '';
        return `${label}${cellStr(row0?.[f.key])}`;
      })
      .filter(Boolean);
    return (
      <tr key={keyPath}>
        <td className="poc-label">{item.title || '항목'}</td>
        <td className="poc-value support-prewrap" colSpan={3}>
          {parts.join(sep) || '—'}
        </td>
      </tr>
    );
  }

  if (item.type === 'ARRAY' || item.dataType === 'ARRAY') {
    const nested = Array.isArray(row0?.[item.key]) ? row0[item.key] : [];
    const cols = arrayColumnsOf(item);
    if (!cols.length) return null;
    return (
      <Fragment key={keyPath}>
        {item.label && (
          <tr>
            <td className="support-section-title" colSpan={4}>
              {item.label}
            </td>
          </tr>
        )}
        <tr>
          <td colSpan={4} style={{ padding: '0.5rem', verticalAlign: 'top' }}>
            <ArrayRowsTable arrayField={item} rows={nested} />
          </td>
        </tr>
      </Fragment>
    );
  }

  if (item.key) {
    return renderFieldRows(item, row0, keyPath);
  }

  return null;
}

function renderBlockTableRows(block, blockIdx, layoutRows) {
  const items = block?.type === 'DIVIDER' ? block.items || [] : block.fields || [];
  const rows = Array.isArray(layoutRows?.[blockIdx]) ? layoutRows[blockIdx] : [{}];
  const row0 = rows[0] || {};

  const out = [];

  const hasTitle =
    block?.type !== 'BARE' &&
    block?.title != null &&
    String(block.title).trim() !== '';

  if (hasTitle) {
    out.push(
      <tr key={`sec-${blockIdx}-title`}>
        <td className="support-section-title" colSpan={4}>
          {block.title}
        </td>
      </tr>
    );
  }

  const sole = items.length === 1 ? items[0] : null;
  if (sole && (sole.type === 'ARRAY' || sole.dataType === 'ARRAY')) {
    out.push(
      <tr key={`sec-${blockIdx}-arr`}>
        <td colSpan={4} style={{ padding: '0.5rem', verticalAlign: 'top' }}>
          {sole.label && <div className="support-bullet-title mb-1">○ {sole.label}</div>}
          <ArrayRowsTable arrayField={sole} rows={rows} />
        </td>
      </tr>
    );
    return out;
  }

  /** flattenLayout 이 SECTION 제목은 블록 title 로 올리고 fields 는 leaf 만 두는 경우가 많아,
   *  장문 필드가 라벨 행을 또 그리면 제목이 두 번 나옴 → 블록 제목과 단일 필드 라벨이 같으면 필드 쪽 제목 생략 */
  const soleHeading = soleFieldHeadingForDedupe(sole);
  const suppressFieldTitleDup =
    hasTitle &&
    items.length === 1 &&
    soleHeading &&
    headingsEqual(block.title, soleHeading);

  items.forEach((it, i) => {
    let r = null;
    if (suppressFieldTitleDup && i === 0) {
      if (it.key) {
        r = renderFieldRows(it, row0, `b${blockIdx}-${i}`, { suppressDuplicateTitle: true });
      } else if (it.type === 'ROW' && Array.isArray(it.fields) && it.fields.length === 1 && it.fields[0]?.key) {
        r = renderFieldRows(it.fields[0], row0, `b${blockIdx}-${i}`, { suppressDuplicateTitle: true });
      }
    }
    if (r == null) {
      r = renderItemRows(it, row0, `b${blockIdx}-${i}`);
    }
    if (r) {
      if (Array.isArray(r)) out.push(...r);
      else out.push(r);
    }
  });

  return out;
}

const DEFAULT_MAIN_TITLE =
  'PoC프로그램 수행 지원 신청';

/** layoutRows 각 블록의 첫 행을 한 객체로 합침 (하단 신청자·제출일 등) */
function mergedFirstRows(layoutRows) {
  const merged = {};
  Object.keys(layoutRows || {}).forEach((k) => {
    const rows = layoutRows[k];
    if (Array.isArray(rows) && rows[0] && typeof rows[0] === 'object') {
      Object.assign(merged, rows[0]);
    }
  });
  return merged;
}

function formatKoreanDate(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
}

/** 지원비 세부 사용계획 아래 정적 안내·작성일·신청자·수신 */
function PocApplicationDocumentStaticFooter({ applicantName, submitDate, signatureDataUrl }) {
  const { t } = useTranslation();
  const name = String(applicantName || '').trim();
  const dateLine = submitDate ? formatKoreanDate(submitDate) : '';

  return (
    <>
      <div className="support-submit-date">{dateLine}</div>

      <div className="support-sign-row">
        <div className="support-sign-name">
          <span className="support-sign-prof">{t('projects.apply.previewFooter.applicant')}</span>
          <span className="name">{name}</span>
          <span className="in">{t('projects.apply.previewFooter.seal')}</span>
          {signatureDataUrl ? (
            <img src={signatureDataUrl} alt={t('popup.previewPopup.signatureAlt')} />
          ) : null}
        </div>
      </div>

      <div className="support-to">한국외국어대학교&nbsp;&nbsp;G-RISE사업단장 귀하</div>
    </>
  );
}

/** 제목 첫 줄은 '…고도화'까지, 둘째 줄은 나머지 (PocProgramSupportPreview와 동일) */
function MainHeadingLines({ text }) {
  const t = String(text || '').trim();
  if (!t) return null;
  const needle = '고도화';
  const i = t.indexOf(needle);
  if (i >= 0) {
    const afterNeedle = i + needle.length;
    const line1 = t.slice(0, afterNeedle).trim();
    const line2 = t.slice(afterNeedle).trim();
    if (line2) {
      return (
        <>
          {line1}
          <br />
          {line2}
        </>
      );
    }
  }
  return <>{t}</>;
}

/**
 * @param {object} props
 * @param {object} props.structureJson — resolvePocApplicationStructureJson 결과 (layout 필수)
 * @param {object} props.dataJson — { layoutRows }
 * @param {string} [props.signatureDataUrl]
 * @param {string} [props.mainTitle] — layoutConfig.title 이 비어 있을 때 쓸 표지 제목 (기본: 공식 신청서 명칭)
 */
export default function PocApplicationLayoutDocumentPreview({
  structureJson,
  dataJson,
  signatureDataUrl,
  mainTitle,
}) {
  const layout = structureJson?.layout || [];
  const layoutRows = useMemo(() => dataJson?.layoutRows || {}, [dataJson]);
  const heading =
    String(structureJson?.title || '').trim() || String(mainTitle || '').trim() || DEFAULT_MAIN_TITLE;
  const subtitle = String(structureJson?.subtitle || '').trim();

  const flatRow = useMemo(() => mergedFirstRows(layoutRows), [layoutRows]);
  const footerApplicantName = useMemo(() => {
    return (
      cellStr(
        flatRow.user_name ||
          flatRow.userName ||
          flatRow.lead_student ||
          flatRow.leadStudent
      ) || ''
    );
  }, [flatRow]);
  const footerSubmitDate = useMemo(() => {
    return (
      flatRow.submit_date ||
      flatRow.submitDate ||
      flatRow.written_date ||
      flatRow.writtenDate ||
      ''
    );
  }, [flatRow]);

  return (
    <div className="poc-preview-shell dfb-preview-shell support-preview-shell">
      <div className="poc-preview-page dfb-preview-page support-preview-page">
        <div className="poc-preview support-preview">
          <div className="support-topline">[서식1] 산학협력 PoC프로그램 수행 지원 신청서</div>

          <table className="poc-table support-info-table support-unified-table">
            <colgroup>
              <col style={{ width: '15%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '35%' }} />
            </colgroup>
            <tbody>
              <tr key="doc-head-title">
                <td className="support-head-logo" colSpan={1}>
                  <div className="support-head-logo-placeholder" aria-hidden="true">
                    G
                  </div>
                </td>
                <td className="support-head-titles" colSpan={3}>
                  <div className="support-title-main">
                    <MainHeadingLines text={heading} />
                  </div>
                  {subtitle ? <div className="support-title-sub">{subtitle}</div> : null}
                </td>
              </tr>
              {layout.flatMap((block, idx) => {
                if (isSupportFundUsagePlanBlock(block)) return [];
                const chunk = renderBlockTableRows(block, idx, layoutRows);
                return chunk.map((el, j) =>
                  React.isValidElement(el) && el.key == null
                    ? React.cloneElement(el, { key: `blk-${idx}-${j}` })
                    : el
                );
              })}
            </tbody>
          </table>

          {layout.map((block, idx) =>
            isSupportFundUsagePlanBlock(block) ? (
              <SupportFundUsagePlanOutsideTable
                key={`support-fund-outside-${idx}`}
                block={block}
                blockIdx={idx}
                layoutRows={layoutRows}
              />
            ) : null
          )}

          <PocApplicationDocumentStaticFooter
            applicantName={footerApplicantName}
            submitDate={footerSubmitDate}
            signatureDataUrl={signatureDataUrl}
          />
        </div>
      </div>
    </div>
  );
}
